import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useSignals, useTopics, useTranscript } from 'src/api/queries';
import { AbsenceState } from 'src/components/editorial/AbsenceState';
import { LoadFailure } from 'src/components/editorial/LoadFailure';
import { Badge } from 'src/components/ui/Badge';
import { Icon } from 'src/components/ui/Icon';
import { Skeleton } from 'src/components/ui/Skeleton';
import {
  EDITORIAL_IMPORTANCE_LABEL,
  EDITORIAL_IMPORTANCE_TONE,
  SENTIMENT_LABEL,
  STANCE_LABEL,
} from 'src/lib/tone';
import { formatClock } from 'src/lib/time';
import type { EditorialImportance, EditorialSignal, EditorialTopicsPayload, EditorialTranscriptTurn } from 'src/types/editorial';

const NO_TRANSCRIPT_TURNS: EditorialTranscriptTurn[] = [];

/**
 * Answers: what came up, how often, and what did the machine make of each block?
 *
 * The two grains read differently on purpose. The tag cloud and entity/claim lists are the
 * whole-stream summary `topics.json` already computes; the per-block list underneath is the
 * signal the summary was built from, richer whenever the run kept it (sentiment, stance, why it
 * matters) and never blocked on that richer data existing — a `--topics-only` run has topics
 * without signals, and this screen must still be useful.
 *
 * The toolbar reshapes what is loaded, like in Claims and Relevance: search across every grain,
 * sort the blocks by their place in the broadcast or by the importance the model assigned them.
 */
export function TopicsWorkspace() {
  const { streamId = '' } = useParams<{ streamId: string }>();
  const topics = useTopics(streamId);
  const signals = useSignals(streamId);
  const transcript = useTranscript(streamId);

  const [query, setQuery] = useState('');
  const [sortBy, setSortBy] = useState<'time' | 'importance'>('time');
  const [activeSection, setActiveSection] = useState<'keywords' | 'entities' | 'claims' | 'blocks'>('keywords');

  const payload = topics.data?.topics?.payload;
  const turns = transcript.data?.transcript?.payload.turns ?? NO_TRANSCRIPT_TURNS;
  const needle = query.trim().toLowerCase();
  const filtering = needle !== '';

  const visibleTopics = useMemo(
    () => payload?.topics.filter(topic => !needle || topic.toLowerCase().includes(needle)) ?? [],
    [payload, needle]
  );
  const visibleEntities = useMemo(
    () =>
      payload?.entities.filter(
        entity => !needle || entity.name.toLowerCase().includes(needle) || entity.type.toLowerCase().includes(needle)
      ) ?? [],
    [payload, needle]
  );
  const visibleKeyClaims = useMemo(
    () =>
      payload?.key_claims.filter(
        claim => !needle || claim.speaker.toLowerCase().includes(needle) || claim.claim.toLowerCase().includes(needle)
      ) ?? [],
    [payload, needle]
  );
  const keywordCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const topic of payload?.topics ?? []) counts.set(topic, countPhraseInTurns(topic, turns));
    return counts;
  }, [payload, turns]);
  const topicBlockCounts = useMemo(() => {
    const counts = new Map<string, number>();
    const blocks = signals.data?.signals.length
      ? signals.data.signals.map(signal => signal.payload.topics)
      : (payload?.chunks ?? []).map(chunk => chunk.topics);
    for (const topic of payload?.topics ?? []) {
      const normalizedTopic = normalizeForSearch(topic);
      counts.set(topic, blocks.filter(blockTopics => blockTopics.some(item => normalizeForSearch(item) === normalizedTopic)).length);
    }
    return counts;
  }, [payload, signals.data]);
  const blockCount = signals.data?.signals.length || payload?.chunks.length || 0;
  const entityMentions = payload?.entities.reduce((total, entity) => total + entity.mentions, 0) ?? 0;

  const sections = [
    { id: 'keywords' as const, label: 'Palabras clave', count: visibleTopics.length },
    { id: 'entities' as const, label: 'Entidades', count: visibleEntities.length },
    { id: 'claims' as const, label: 'Claims destacados', count: visibleKeyClaims.length },
    { id: 'blocks' as const, label: 'Por bloque', count: blockCount },
  ];

  // The section shell renders in every state — pending, error, empty — so the page never falls
  // back to a bare panel without its heading.
  return (
    <div className="page-medium topics-workspace">
      <header className="workspace-section-head topics-section-head" aria-labelledby="topics-title">
        <div className="workspace-section-head__copy">
          <p className="workspace-section-head__eyebrow">CONTEXTO DE LA TRANSMISIÓN</p>
          <h2 id="topics-title">Temas</h2>
          <p>
            Explora las palabras clave y cuántas veces aparecen en el guion, las entidades mencionadas, los claims
            destacados y la lectura de cada bloque.
          </p>
        </div>
        <div className="workspace-section-head__stats" aria-label="Resumen de temas">
          <div className="workspace-section-head__stat">
            <strong>{payload ? payload.topics.length : '—'}</strong>
            <span>palabras clave</span>
          </div>
          <div className="workspace-section-head__stat">
            <strong>{payload ? payload.entities.length : '—'}</strong>
            <span>entidades</span>
          </div>
          <div className="workspace-section-head__stat">
            <strong>{payload ? payload.key_claims.length : '—'}</strong>
            <span>claims destacados</span>
          </div>
          <div className="workspace-section-head__stat">
            <strong>{payload ? blockCount : '—'}</strong>
            <span>bloques analizados</span>
          </div>
        </div>
      </header>

      {payload && (
        <nav className="topics-nav" aria-label="Secciones de temas">
          {sections.map(section => (
            <button
              key={section.id}
              type="button"
              className="topics-nav__item"
              aria-pressed={activeSection === section.id}
              onClick={() => setActiveSection(section.id)}
            >
              <span>{section.label}</span>
              <span className="topics-nav__count">{section.count}</span>
            </button>
          ))}
        </nav>
      )}

      <section className="tx-filters" aria-label="Filtros de temas">
        <div className="tx-field tx-search">
          <label htmlFor="topics-q">Buscar</label>
          <div className="tx-search-box">
            <span className="tx-search-icon" aria-hidden="true">
              <Icon name="search" />
            </span>
            <input
              id="topics-q"
              type="search"
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder="Buscar tema, entidad o claim"
              autoComplete="off"
            />
          </div>
        </div>
        {activeSection === 'blocks' && (
          <div className="tx-field tx-field--select">
            <label htmlFor="topics-sort">Ordenar bloques</label>
            <select
              id="topics-sort"
              value={sortBy}
              onChange={event => setSortBy(event.target.value as 'time' | 'importance')}
            >
              <option value="time">Por momento</option>
              <option value="importance">Por importancia</option>
            </select>
          </div>
        )}
        <button
          className="tx-clear"
          type="button"
          onClick={() => {
            setQuery('');
            setSortBy('time');
          }}
          disabled={!filtering && sortBy === 'time'}
        >
          Limpiar
        </button>
      </section>

      {topics.isPending ? (
        <Skeleton variant="rows" count={6} />
      ) : topics.isError ? (
        <LoadFailure title="No se pudieron cargar los temas" error={topics.error} onRetry={topics.refetch} />
      ) : !payload ? (
        <AbsenceState
          kind="not_reached"
          what="No hay temas para esta transmisión."
          why="Los temas se calculan al cerrar el stream, sobre las señales de cada bloque."
        />
      ) : (
        <>
          {activeSection === 'keywords' && (
            <section className="topics-panel" aria-labelledby="topics-panel-title">
              <div className="topics-panel__head">
                <div>
                  <h3 id="topics-panel-title">Palabras clave</h3>
                  <p>Frase exacta en el guion y número de bloques donde se detectó el tema.</p>
                </div>
                <span className="topics-panel__total">{visibleTopics.length} términos</span>
              </div>
              {visibleTopics.length ? (
                <ul className="topic-keywords">
                  {visibleTopics.map(topic => (
                    <li className="topic-keywords__item" key={topic}>
                      <span className="topic-keywords__name">{topic}</span>
                      <strong className="topic-keywords__count">{transcript.data?.transcript ? keywordCounts.get(topic) : '—'}</strong>
                      <span className="topic-keywords__unit">
                        {transcript.data?.transcript
                          ? keywordCountLabel(keywordCounts.get(topic) ?? 0)
                          : 'frecuencia pendiente'}
                      </span>
                      <span className="topic-keywords__blocks">
                        {topicBlockCounts.get(topic) ?? 0} {topicBlockCounts.get(topic) === 1 ? 'bloque relacionado' : 'bloques relacionados'}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="topics-panel__empty">No hay palabras clave que coincidan con la búsqueda.</p>
              )}
              {!transcript.data?.transcript && (
                <p className="topics-panel__note">
                  {transcript.isError
                    ? 'No se pudo consultar el guion; las frecuencias no están disponibles ahora.'
                    : 'El guion aún no está disponible; las frecuencias aparecerán cuando se cargue.'}
                </p>
              )}
            </section>
          )}

          {activeSection === 'entities' && (
            <section className="topics-panel" aria-labelledby="topics-panel-title">
              <div className="topics-panel__head">
                <div>
                  <h3 id="topics-panel-title">Entidades más mencionadas</h3>
                  <p>Personas, instituciones y lugares detectados en la transmisión.</p>
                </div>
                <span className="topics-panel__total">{filtering ? `${visibleEntities.length} de ${payload.entities.length}` : `${payload.entities.length} · ${entityMentions} menciones`}</span>
              </div>
              {visibleEntities.length ? (
                <EntityList entities={visibleEntities} />
              ) : (
                <p className="topics-panel__empty">No hay entidades que coincidan con la búsqueda.</p>
              )}
            </section>
          )}

          {activeSection === 'claims' && (
            <section className="topics-panel" aria-labelledby="topics-panel-title">
              <div className="topics-panel__head">
                <div>
                  <h3 id="topics-panel-title">Claims destacados</h3>
                  <p>Afirmaciones que resumen los puntos centrales de la transmisión.</p>
                </div>
                <span className="topics-panel__total">{filtering ? `${visibleKeyClaims.length} de ${payload.key_claims.length}` : `${payload.key_claims.length} claims`}</span>
              </div>
              {visibleKeyClaims.length ? (
                <KeyClaimList claims={visibleKeyClaims} />
              ) : (
                <p className="topics-panel__empty">No hay claims que coincidan con la búsqueda.</p>
              )}
            </section>
          )}

          {activeSection === 'blocks' && (
            <section className="topics-panel" aria-labelledby="topics-panel-title">
              <div className="topics-panel__head">
                <div>
                  <h3 id="topics-panel-title">Información por bloque</h3>
                  <p>Resumen, temas y contexto editorial de cada tramo de la transmisión.</p>
                </div>
                <span className="topics-panel__total">{blockCount} bloques</span>
              </div>
              {signals.data?.signals && signals.data.signals.length > 0 ? (
                <SignalBlocks signals={signals.data.signals} needle={needle} sortBy={sortBy} />
              ) : (
                <TopicChunks chunks={payload.chunks} needle={needle} />
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
}

function normalizeForSearch(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
}

/** Count exact keyword mentions in the transcript, case/accent-insensitively and on word edges. */
function countPhraseInTurns(phrase: string, turns: Array<{ text: string }>): number {
  const normalized = normalizeForSearch(phrase).trim();
  if (!normalized) return 0;
  const escaped = normalized.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const matcher = new RegExp(`(?<![\\p{L}\\p{N}])${escaped}(?![\\p{L}\\p{N}])`, 'gu');
  return turns.reduce((total, turn) => total + (normalizeForSearch(turn.text).match(matcher)?.length ?? 0), 0);
}

function keywordCountLabel(count: number): string {
  return count === 1 ? 'vez en guion' : 'veces en guion';
}

const IMPORTANCE_RANK: Record<EditorialImportance, number> = { low: 0, medium: 1, high: 2 };
const TOPIC_BLOCKS_PER_PAGE = 8;

/** Show every entity at once; this is usually a short, ranked summary rather than a long feed. */
function EntityList({ entities }: { entities: EditorialTopicsPayload['entities'] }) {
  const maxMentions = Math.max(...entities.map(entity => entity.mentions), 1);
  const typeLabels: Record<string, string> = {
    person: 'Persona',
    institution: 'Institución',
    organization: 'Organización',
    location: 'Lugar',
    event: 'Evento',
  };

  return (
    <ul className="entity-grid">
      {entities.map(entity => (
        <li className="entity-card" key={entity.name}>
          <div className="entity-card__topline">
            <span className="entity-card__type">{typeLabels[entity.type.toLowerCase()] ?? entity.type}</span>
            <span className="entity-card__mentions">
              <strong>{entity.mentions}</strong>
              <span>{entity.mentions === 1 ? 'mención' : 'menciones'}</span>
            </span>
          </div>
          <h4 className="entity-card__name">{entity.name}</h4>
          <span
            className="entity-card__meter"
            role="meter"
            aria-label={`${entity.name}: ${entity.mentions} menciones`}
            aria-valuenow={entity.mentions}
            aria-valuemin={0}
            aria-valuemax={maxMentions}
          >
            <span style={{ inlineSize: `${(entity.mentions / maxMentions) * 100}%` }} />
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Claims stay verbatim, but each gets an independent reading card and a short page. */
function KeyClaimList({ claims }: { claims: EditorialTopicsPayload['key_claims'] }) {
  const [page, setPage] = useState(0);
  useEffect(() => setPage(0), [claims]);
  const pageCount = Math.ceil(claims.length / TOPIC_BLOCKS_PER_PAGE);
  const currentPage = Math.min(page, pageCount - 1);
  const visible = claims.slice(currentPage * TOPIC_BLOCKS_PER_PAGE, (currentPage + 1) * TOPIC_BLOCKS_PER_PAGE);

  return (
    <>
      <BlockPager
        page={currentPage}
        pageCount={pageCount}
        total={claims.length}
        label="Claims"
        ariaLabel="Páginas de claims destacados"
        onPageChange={setPage}
      />
      <ul className="key-claims-grid">
        {visible.map((claim, index) => (
          <li className="key-claim-card" key={`${claim.timestamp}-${index}`}>
            <header className="key-claim-card__meta">
              <span className="key-claim-card__speaker">
                {claim.speaker === 'Unknown' || !claim.speaker ? 'Voz no identificada' : claim.speaker}
              </span>
              {claim.timestamp && <time className="key-claim-card__time">{claim.timestamp}</time>}
            </header>
            <blockquote className="key-claim-card__quote">{claim.claim}</blockquote>
          </li>
        ))}
      </ul>
    </>
  );
}

function signalMatches(payload: EditorialSignal['payload'], needle: string): boolean {
  return (
    payload.summary.toLowerCase().includes(needle) ||
    payload.topics.some(topic => topic.toLowerCase().includes(needle)) ||
    (payload.entities ?? []).some(entity => entity.name.toLowerCase().includes(needle)) ||
    (payload.stance ?? []).some(stance => stance.target.toLowerCase().includes(needle)) ||
    (payload.why_it_matters ?? '').toLowerCase().includes(needle) ||
    (payload.possible_news_angle ?? '').toLowerCase().includes(needle)
  );
}

/** The richer view — used whenever the run kept per-chunk signals, not only their topics.json rollup. */
function SignalBlocks({
  signals,
  needle,
  sortBy,
}: {
  signals: EditorialSignal[];
  needle: string;
  sortBy: 'time' | 'importance';
}) {
  const [page, setPage] = useState(0);
  const ordered = useMemo(() => {
    const visible = needle ? signals.filter(signal => signalMatches(signal.payload, needle)) : signals;
    if (sortBy === 'importance') {
      // Failed extractions carry no reading to rank — they hold the tail, in time order.
      return [...visible].sort(
        (a, b) =>
          (a.payload.extraction_failed ? -1 : IMPORTANCE_RANK[a.payload.editorial_importance]) -
            (b.payload.extraction_failed ? -1 : IMPORTANCE_RANK[b.payload.editorial_importance]) ||
          (a.payload.start_seconds ?? 0) - (b.payload.start_seconds ?? 0)
      );
    }
    return [...visible].sort((a, b) => (a.payload.start_seconds ?? 0) - (b.payload.start_seconds ?? 0));
  }, [signals, needle, sortBy]);
  useEffect(() => setPage(0), [needle, sortBy]);

  if (ordered.length === 0) {
    return <p className="guion-empty">Ningún bloque coincide con los filtros.</p>;
  }

  const pageCount = Math.ceil(ordered.length / TOPIC_BLOCKS_PER_PAGE);
  const currentPage = Math.min(page, pageCount - 1);
  const visible = ordered.slice(currentPage * TOPIC_BLOCKS_PER_PAGE, (currentPage + 1) * TOPIC_BLOCKS_PER_PAGE);

  return (
    <>
      <BlockPager page={currentPage} pageCount={pageCount} total={ordered.length} onPageChange={setPage} />
      <ul className="signal-blocks">
        {visible.map(signal => {
          const payload = signal.payload;

          if (payload.extraction_failed) {
            return (
              <li key={signal.chunkId} className="signal-block" data-failed="true">
                <header className="signal-block__head">
                  {payload.start_seconds !== undefined && (
                    <span className="mono">{formatClock(payload.start_seconds)}</span>
                  )}
                  <Badge tone="contradicted">Sin analizar</Badge>
                </header>
                <p className="signal-block__note signal-block__note--failed">
                  El modelo no respondió para este tramo — no es que no se haya dicho nada, es que no se pudo leer.
                  Vuelve a correr la etapa de señales para reintentarlo.
                </p>
              </li>
            );
          }

          return (
            <li key={signal.chunkId} className="signal-block">
              <header className="signal-block__head">
                {payload.start_seconds !== undefined && (
                  <span className="signal-block__time mono">{formatClock(payload.start_seconds)}</span>
                )}
                <Badge tone={EDITORIAL_IMPORTANCE_TONE[payload.editorial_importance]}>
                  {EDITORIAL_IMPORTANCE_LABEL[payload.editorial_importance]}
                </Badge>
                {payload.sentiment && (
                  <span className="signal-block__sentiment mono">{SENTIMENT_LABEL[payload.sentiment.general]}</span>
                )}
              </header>

              <p className="signal-block__summary-label">Resumen del bloque</p>
              <p className="reading signal-block__summary">{payload.summary}</p>

              {payload.topics.length > 0 && (
                <div className="signal-block__topics">
                  <span className="signal-block__label">Temas</span>
                  <ul className="topic-tags topic-tags--small">
                    {payload.topics.map(topic => (
                      <li key={topic} className="topic-tag">
                        {topic}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {payload.entities && payload.entities.length > 0 && (
                <div className="signal-block__entities">
                  <span className="signal-block__label">Entidades</span>
                  <ul className="signal-block__entity-tags">
                    {payload.entities.map(entity => (
                      <li key={`${entity.name}-${entity.type}`}>
                        <strong>{entity.name}</strong>
                        <span>{entity.type}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {payload.stance && payload.stance.length > 0 && (
                <div className="signal-block__stance-wrap">
                  <span className="signal-block__label">Postura</span>
                  <ul className="signal-block__stance" aria-label="Postura por entidad">
                    {payload.stance.map(stance => (
                      <li key={stance.target}>
                        <span className="signal-block__stance-target">{stance.target}</span>
                        <span className="signal-block__stance-position">{STANCE_LABEL[stance.position]}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {(payload.why_it_matters || payload.possible_news_angle) && (
                <div className="signal-block__editorial">
                  {payload.why_it_matters && (
                    <div className="signal-block__editorial-item">
                      <span className="signal-block__label">Por qué importa</span>
                      <p>{payload.why_it_matters}</p>
                    </div>
                  )}
                  {payload.possible_news_angle && (
                    <div className="signal-block__editorial-item signal-block__editorial-item--angle">
                      <span className="signal-block__label">Posible ángulo</span>
                      <p>{payload.possible_news_angle}</p>
                    </div>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
}

function chunkMatches(chunk: EditorialTopicsPayload['chunks'][number], needle: string): boolean {
  return (
    chunk.summary.toLowerCase().includes(needle) ||
    chunk.topics.some(topic => topic.toLowerCase().includes(needle)) ||
    chunk.entities.some(entity => entity.toLowerCase().includes(needle))
  );
}

/** The fallback view — topics.json's own per-chunk rollup, all that exists after a `--topics-only` run. */
function TopicChunks({ chunks, needle }: { chunks: EditorialTopicsPayload['chunks']; needle: string }) {
  const [page, setPage] = useState(0);
  const visible = needle ? chunks.filter(chunk => chunkMatches(chunk, needle)) : chunks;
  useEffect(() => setPage(0), [needle]);

  if (visible.length === 0) {
    return chunks.length === 0 ? (
      <AbsenceState kind="empty" what="El desglose por bloque llegó vacío." />
    ) : (
      <p className="guion-empty">Ningún bloque coincide con los filtros.</p>
    );
  }

  const pageCount = Math.ceil(visible.length / TOPIC_BLOCKS_PER_PAGE);
  const currentPage = Math.min(page, pageCount - 1);
  const pageItems = visible.slice(currentPage * TOPIC_BLOCKS_PER_PAGE, (currentPage + 1) * TOPIC_BLOCKS_PER_PAGE);

  return (
    <>
      <BlockPager page={currentPage} pageCount={pageCount} total={visible.length} onPageChange={setPage} />
      <ul className="signal-blocks">
        {pageItems.map((chunk, index) => (
          <li key={`${chunk.start}-${index}`} className="signal-block">
            <header className="signal-block__head">
              <span className="signal-block__time mono">{chunk.start}</span>
              <Badge tone="info">Bloque analizado</Badge>
            </header>
            <p className="signal-block__summary-label">Resumen del bloque</p>
            <p className="reading signal-block__summary">{chunk.summary}</p>
            {chunk.topics.length > 0 && (
              <div className="signal-block__topics">
                <span className="signal-block__label">Temas</span>
                <ul className="topic-tags topic-tags--small">
                  {chunk.topics.map(topic => (
                    <li key={topic} className="topic-tag">
                      {topic}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {chunk.entities.length > 0 && (
              <div className="signal-block__entities">
                <span className="signal-block__label">Entidades</span>
                <ul className="signal-block__entity-tags">
                  {chunk.entities.map(entity => (
                    <li key={entity}><strong>{entity}</strong></li>
                  ))}
                </ul>
              </div>
            )}
          </li>
        ))}
      </ul>
    </>
  );
}

function BlockPager({
  page,
  pageCount,
  total,
  label = 'Bloques',
  ariaLabel = 'Páginas de bloques',
  onPageChange,
}: {
  page: number;
  pageCount: number;
  total: number;
  label?: string;
  ariaLabel?: string;
  onPageChange: (page: number) => void;
}) {
  if (pageCount <= 1) return null;
  const first = page * TOPIC_BLOCKS_PER_PAGE + 1;
  const last = Math.min((page + 1) * TOPIC_BLOCKS_PER_PAGE, total);

  return (
    <nav className="topics-pager" aria-label={ariaLabel}>
      <span>{label} {first}–{last} de {total}</span>
      <div className="topics-pager__actions">
        <button type="button" className="topics-pager__button" disabled={page === 0} onClick={() => onPageChange(page - 1)}>
          Anterior
        </button>
        <span className="topics-pager__page">{page + 1} / {pageCount}</span>
        <button
          type="button"
          className="topics-pager__button"
          disabled={page >= pageCount - 1}
          onClick={() => onPageChange(page + 1)}
        >
          Siguiente
        </button>
      </div>
    </nav>
  );
}
